import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../store/auth';
import { formatDateOnly } from '../../utils/formatData';
import { PageHeadline } from '../../components/atoms';
import { ButtonActionRow } from './CompanyDocuments/styleGuide';
import { useAppSelector } from '../../hooks/hooksRedux';
import { RootState } from '../../store';
import { defaultBorder, defaultColors } from '../../styles/style-guide';
import { differenceInDays, parseISO, isBefore } from 'date-fns';
import { DashboardCustomerContext } from './Context';
import { EditDirectorModal } from './components/EditDirectorModal';
import { EditShareholderModal } from './components/EditShareholderModal';
import PassportUploadModal from './components/PassportUploadModal';

const DashboardDirectorsPreferences = () => {
  const [shareholders, setShareholders] = useState<any>([]);
  const [openDirector, setOpenDirector] = useState<boolean>(false);
  const [openShareholder, setOpenShareholder] = useState<boolean>(false);
  const [openPassportUpload, setOpenPassportUpload] = useState<boolean>(false);
  const [director, setDirector] = useState<any>();

  const { company } = useAuth();
  const { t, i18n } = useTranslation();
  const { companyInfo, refreshCompanyInfo } = useContext(DashboardCustomerContext);

  const { colors, border } = useAppSelector((state: RootState) => state.styleGuide.data);

  const dateLocale = useMemo(() => {
    const language = i18n.language || 'en';
    if (language.startsWith('pt')) return 'pt-BR';
    if (language.startsWith('es')) return 'es-ES';
    return 'en-US';
  }, [i18n.language]);

  const primaryColor = colors.primary || defaultColors.primary;
  const borderPrimaryColor = border.primary || defaultBorder.primary;

  // Função para verificar se o passaporte está próximo do vencimento ou vencido
  // Permite upload a partir de 30 dias antes do vencimento
  // Se não houver passaporte, permite upload (primeira vez)
  const getPassportStatus = (expiryDate: string) => {
    if (!expiryDate) return { status: 'unknown', daysLeft: 0, isExpired: false, canUpload: true, showButton: true };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Resetar horas para comparação precisa
    const expiry = parseISO(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    const daysLeft = differenceInDays(expiry, today);
    
    // Passaporte está vencido se a data de expiração é anterior ou igual a hoje
    const isExpired = isBefore(expiry, today) || daysLeft <= 0;
    
    // Permite upload se estiver vencido OU se faltarem 30 dias ou menos para vencer
    const canUpload = isExpired || daysLeft <= 30;
    const showButton = canUpload; // Botão só aparece quando pode fazer upload
    
    if (isExpired) {
      return { status: 'expired', daysLeft: Math.abs(daysLeft), isExpired: true, canUpload: true, showButton: true };
    } else if (daysLeft <= 30) {
      return { status: 'near-expiry', daysLeft, isExpired: false, canUpload: true, showButton: true };
    } else {
      return { status: 'valid', daysLeft, isExpired: false, canUpload: false, showButton: false };
    }
  };

  const handleRefreshShareholders = useCallback(async () => {
    if (!company?.id) {
      return;
    }

    await refreshCompanyInfo(true);
  }, [company?.id, refreshCompanyInfo]);

  useEffect(() => {
    if (!company?.id) {
      setShareholders([]);
      return;
    }

    if (!companyInfo?.shareholders?.length) {
      void refreshCompanyInfo();
      return;
    }

    setShareholders(companyInfo.shareholders);
  }, [company?.id, companyInfo?.shareholders, refreshCompanyInfo]);

  const handleEdit = (shareholder: any) => {
    setDirector(shareholder);
    // Verificar se é diretor baseado no campo is_director (pode ser number, string ou boolean)
    const isDirector = shareholder.is_director === 1 || 
                      shareholder.is_director === true || 
                      shareholder.is_director === '1' ||
                      shareholder.is_director === 'true';
    
    if (isDirector) {
      setOpenDirector(true);
    } else {
      setOpenShareholder(true);
    }
  };

  const handlePassportUpload = (shareholder: any) => {
    setDirector(shareholder);
    setOpenPassportUpload(true);
  };

  const getTypeIcon = (shareholder: any) => {
    // Verificar se é diretor baseado no campo is_director (pode ser number, string ou boolean)
    const isDirector = shareholder.is_director === 1 || 
                      shareholder.is_director === true || 
                      shareholder.is_director === '1' ||
                      shareholder.is_director === 'true';
    
    if (isDirector) {
      return (
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
      );
    }
  };

  return (
    <>
      <PageHeadline title={t('labels.lblDirectorsAndShareholders')} />
      <EditDirectorModal
        open={openDirector}
        onOpenChange={setOpenDirector}
        director={director}
        onChange={handleRefreshShareholders}
      />
      <EditShareholderModal
        open={openShareholder}
        onOpenChange={setOpenShareholder}
        director={director}
        onChange={handleRefreshShareholders}
      />
      <PassportUploadModal
        open={openPassportUpload}
        onOpenChange={setOpenPassportUpload}
        director={director}
        onChange={handleRefreshShareholders}
      />

      {/* Cards Layout */}
      <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {shareholders.map((shareholder: any, index: number) => {
          const passportStatus = getPassportStatus(shareholder.passports?.[0]?.expiry_date);
          const statusText = passportStatus.isExpired
            ? t('documents.passportExpired', { days: passportStatus.daysLeft })
            : passportStatus.status === 'near-expiry'
            ? t('documents.passportNearExpiry', { days: passportStatus.daysLeft })
            : t('documents.passportValid', { days: passportStatus.daysLeft });
          const statusColor = passportStatus.isExpired
            ? 'text-red-600'
            : passportStatus.status === 'near-expiry'
            ? 'text-yellow-600'
            : 'text-green-600';
          
          return (
            <div key={index} className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow">
              {/* Header com avatar e tipo */}
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex-shrink-0">
                  {getTypeIcon(shareholder)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {shareholder.name}
                  </h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    shareholder.is_director ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {shareholder.is_director ? t('labels.lblDirector') : t('labels.lblShareholder')}
                  </span>
                </div>
              </div>

              {/* Informações principais */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  <span className="truncate">{shareholder.email}</span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{formatDateOnly(shareholder.created_at, dateLocale)}</span>
                </div>
                
                {/* Status do Passaporte */}
                {shareholder.passports && shareholder.passports.length > 0 && (
                  <div className="flex items-center text-sm">
                    <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="flex flex-col">
                      <span className={`font-medium ${statusColor}`}>{statusText}</span>
                      <span className="text-xs text-gray-500">
                        {t('documents.passportExpiry', {
                          date: formatDateOnly(shareholder.passports[0].expiry_date, dateLocale)
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Botões de ação */}
              <div className="flex flex-col gap-2">
                {company?.status !== 'approved' && (
                  <ButtonActionRow
                    $borderColors={borderPrimaryColor}
                    $colors={primaryColor}
                    onClick={() => {
                      handleEdit(shareholder);
                    }}
                  >
                    {t('labels.lblView')}
                  </ButtonActionRow>
                )}
                
                {passportStatus.showButton && (
                  <ButtonActionRow
                    $borderColors="#3B82F6"
                    $colors="#3B82F6"
                    onClick={() => {
                      handlePassportUpload(shareholder);
                    }}
                  >
                    {t('documents.uploadPassport')}
                  </ButtonActionRow>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default DashboardDirectorsPreferences;